import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import type { AuthGateway } from '../application/AuthGateway'
import { AuthFailure, type OperatorSession } from '../domain/session'

// Adapté de reca-app-v3 (src/features/auth/infrastructure/supabase/SupabaseAuthGateway.ts) : même session serveur
// `current_app_session()`, mêmes refus (désactivé, révoqué, mot de passe temporaire).

const appSessionSchema = z.object({
  userId: z.string(),
  displayName: z.string(),
  email: z.string(),
  status: z.string(),
  employeeId: z.string().nullable().optional(),
  canOperate: z.boolean(),
})

const replacementFailures: Record<string, string> = {
  CURRENT_PASSWORD_INVALID: 'Le mot de passe temporaire saisi est incorrect.',
  WEAK_PASSWORD:
    'Choisissez au moins 12 caractères, avec trois types parmi minuscules, majuscules, chiffres, symboles.',
  PASSWORD_UNCHANGED: 'Le nouveau mot de passe doit être différent du mot de passe temporaire.',
  PASSWORD_CHANGE_NOT_REQUIRED: 'Votre mot de passe a déjà été remplacé. Reconnectez-vous.',
}

async function functionErrorCode(error: unknown): Promise<string | undefined> {
  const context = (error as { context?: unknown } | null)?.context
  if (!(context instanceof Response)) return undefined
  const body = (await context.json().catch(() => null)) as { error?: unknown } | null
  return typeof body?.error === 'string' ? body.error : undefined
}

function userAgent(): string | undefined {
  return typeof navigator === 'undefined' ? undefined : navigator.userAgent.slice(0, 300)
}

export function createSupabaseAuthGateway(client: SupabaseClient): AuthGateway {
  async function resolve(): Promise<OperatorSession | null> {
    const { data, error } = (await client.rpc('current_app_session')) as unknown as { data: unknown; error: unknown }
    if (error) throw new AuthFailure('UNEXPECTED', 'Le serveur ne répond pas. Réessayez dans un instant.')
    if (data === null) return null
    const parsed = appSessionSchema.safeParse(data)
    if (!parsed.success) {
      throw new AuthFailure('ACCOUNT_SETUP_REQUIRED', 'Votre compte n’est pas configuré. Prévenez le bureau.')
    }
    const session = parsed.data
    if (session.status === 'SESSION_REVOKED') {
      await client.auth.signOut({ scope: 'local' })
      throw new AuthFailure('SESSION_EXPIRED', 'Votre session a été fermée par le bureau. Reconnectez-vous.')
    }
    if (session.status === 'DISABLED') {
      await client.auth.signOut({ scope: 'local' })
      throw new AuthFailure('USER_INACTIVE', 'Ce compte est désactivé. Adressez-vous au bureau.')
    }
    if (session.status !== 'ACTIVE' && session.status !== 'PASSWORD_CHANGE_REQUIRED') {
      throw new AuthFailure('USER_INACTIVE', 'Votre accès est temporairement suspendu.')
    }
    return {
      userId: session.userId,
      email: session.email,
      displayName: session.displayName,
      status: session.status,
      canOperate: session.canOperate,
      employeeId: session.employeeId ?? null,
    }
  }

  async function audit(eventType: 'LOGIN_SUCCEEDED' | 'LOGOUT') {
    const agent = userAgent()
    await client
      .rpc('record_security_event', { event_type: eventType, ...(agent ? { event_metadata: { userAgent: agent } } : {}) })
      .then(
        () => undefined,
        () => undefined,
      )
  }

  return {
    async getSession() {
      const { data } = await client.auth.getSession()
      return data.session ? resolve() : null
    },
    async signIn(identifier, password) {
      const { data, error } = await client.auth.signInWithPassword({ email: identifier.trim().toLowerCase(), password })
      if (error?.status === 429) {
        throw new AuthFailure('UNEXPECTED', 'Trop de tentatives. Patientez quelques minutes avant de réessayer.')
      }
      if (error && error.status !== 400) {
        throw new AuthFailure('UNEXPECTED', 'La connexion au service a échoué. Vérifiez le réseau et réessayez.')
      }
      if (!data.session) {
        await client
          .rpc('record_login_failure_v1', { p_identifier: identifier, p_user_agent: userAgent() ?? null })
          .then(
            () => undefined,
            () => undefined,
          )
        throw new AuthFailure('INVALID_CREDENTIALS', 'Identifiant ou mot de passe invalide.')
      }
      try {
        const session = await resolve()
        if (!session) throw new AuthFailure('ACCOUNT_SETUP_REQUIRED', 'Votre compte n’est pas configuré.')
        await audit('LOGIN_SUCCEEDED')
        return session
      } catch (failure) {
        await client.auth.signOut({ scope: 'local' })
        throw failure
      }
    },
    async signOut() {
      await audit('LOGOUT')
      await client.auth.signOut({ scope: 'local' })
    },
    async replaceTemporaryPassword(currentPassword, newPassword) {
      const { data: sessionData } = await client.auth.getSession()
      const email = sessionData.session?.user.email
      if (!email) throw new AuthFailure('SESSION_EXPIRED', 'Votre session a expiré. Reconnectez-vous.')
      const invocation = await client.functions.invoke('employee-accounts', {
        body: { action: 'replace-temporary-password', currentPassword, newPassword },
      })
      if (invocation.error) {
        const code = await functionErrorCode(invocation.error)
        throw new AuthFailure(
          'UNEXPECTED',
          (code ? replacementFailures[code] : undefined) ??
            'Le mot de passe n’a pas pu être remplacé. Réessayez dans un instant.',
        )
      }
      // Toutes les sessions viennent d'être révoquées : on se reconnecte avec le nouveau mot de passe.
      await client.auth.signOut({ scope: 'local' })
      const { error } = await client.auth.signInWithPassword({ email, password: newPassword })
      if (error) throw new AuthFailure('SESSION_EXPIRED', 'Mot de passe remplacé. Reconnectez-vous avec le nouveau.')
      const session = await resolve()
      if (!session) throw new AuthFailure('SESSION_EXPIRED', 'Reconnectez-vous avec le nouveau mot de passe.')
      return session
    },
  }
}
